import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import BlackDotContainer from './BlackDotContainer';
import DigitsContainer, { DigitsContainerInputType } from '../../../generic-components/DigitsContainer';
import { IPayload, useActivateCardQuery } from '../../../hooks/query/useActivateCardQuery';
import { ACTIVATE_STYLES_TEXTS, ACTIVATION_FLOWS } from '../constants';
import { useStore } from '../../../store';
import { makeStepBack } from '../helpers/navigateHelper';
import { VIEWS_MAP } from '../../../router/constants';
import { getMostRelevantCard } from '../../../helpers/cardStatus.helper';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';

interface IProps {
  goToStep: (step: number) => void;
}

const ActivationWithSsn = ({ goToStep }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { customerId } = useStore((state) => state.currUser());
  const { getPersonalAccount } = useStore((state) => state);
  const summary = useStore((state) => state.summary);
  const {
    customer: { nationalIdDescription },
  } = summary;
  const cardData = getMostRelevantCard(getPersonalAccount().cards);
  const { SSN } = ACTIVATION_FLOWS;
  const { CARD_TRACKER } = VIEWS_MAP;

  const [data, setData] = useState({ lastFourCard: '', lastFourSSN: '' });
  const [isError, setIsError] = useState<boolean>(false);

  const getPayload = () => {
    const res: IPayload = { last4Card: '' };
    res.last4Card = data.lastFourCard;
    res.last4SSN = data.lastFourSSN;
    return res;
  };

  const disabledContinueButton = !(data.lastFourCard.length === 4 && data.lastFourSSN.length === 4);
  const { refetch: getSummaryData, isLoading: isRefetchingSummary } = useAccountSummaryQuery(() => { }, '', 'activateCardRefreshSummary');

  const { refetch: activateCard, isLoading: isActivationInProcess } = useActivateCardQuery({
    customerId,
    activationType: SSN,
    cardId: cardData.cardId,
    payload: getPayload(),
    onSuccess: async () => {
      getSummaryData().then(() => {
        goToStep(2);
      });
    },
    onFailure: () => {
      setIsError(true);
    },
  });

  const handleFulfillSSN = (ssn: string) => {
    setData({ ...data, lastFourSSN: ssn });
  };
  const handleFulfillCardLast4 = (last4: string) => {
    setData({ ...data, lastFourCard: last4 });
  };

  return (
    <div className={ACTIVATE_STYLES_TEXTS.CONTAINER}>
      <span className={ACTIVATE_STYLES_TEXTS.HEADER_STYLE}>{t(ACTIVATE_STYLES_TEXTS.HEADER)}</span>
      <span className={ACTIVATE_STYLES_TEXTS.TEXT_STYLE}>{t(ACTIVATE_STYLES_TEXTS.TEXT)}</span>
      <span className={ACTIVATE_STYLES_TEXTS.PLACEHOLDER_STYLE}>
        {t('ACTIVATE_CARD_STEP1_SSN_PLACEHOLDER', { identityCard: nationalIdDescription || 'SSN' })}
      </span>

      <div className={ACTIVATE_STYLES_TEXTS.DIGITS_CONTAINER_SSN_WRAPPER}>
        <BlackDotContainer
          containerClassName={ACTIVATE_STYLES_TEXTS.BLACK_DOT_CONTAINER}
          dotsAmount={5}
          dotsContainerClassName=''
          dotsInGroup={3}
        />
        <DigitsContainer
          needCleanParentState
          resetIsError={() => {
            setIsError(false);
          }}
          containerClassName={ACTIVATE_STYLES_TEXTS.DIGITS_CONTAINER}
          isError={false}
          isLoading={false}
          numOfDigits={4}
          onFulfill={handleFulfillSSN}
          inputType={
            nationalIdDescription ? DigitsContainerInputType.NUMBER_AND_LETTERS : DigitsContainerInputType.NUMBERS
          }
        />
      </div>
      <span className={ACTIVATE_STYLES_TEXTS.PLACEHOLDER_STYLE}>{t(ACTIVATE_STYLES_TEXTS.PLACEHOLDER_LAST_4)}</span>
      <div className={ACTIVATE_STYLES_TEXTS.DIGITS_CONTAINER_LAST_4_WRAPPER}>
        <BlackDotContainer
          containerClassName={ACTIVATE_STYLES_TEXTS.BLACK_DOT_CONTAINER}
          dotsAmount={12}
          dotsContainerClassName=''
          dotsInGroup={4}
        />
        <DigitsContainer
          needCleanParentState
          resetIsError={() => {
            setIsError(false);
          }}
          containerClassName={ACTIVATE_STYLES_TEXTS.DIGITS_CONTAINER}
          isError={false}
          isLoading={false}
          numOfDigits={4}
          onFulfill={handleFulfillCardLast4}
          inputType={DigitsContainerInputType.NUMBERS}
          focusOnStart={false}
        />
      </div>
      <div className={ACTIVATE_STYLES_TEXTS.BUTTONS_ERRORS_CONTAINER_SSN}>
        <div data-testid='ONC_4lastDigitsError' className={ACTIVATE_STYLES_TEXTS.ERRORS_CONTAINER_STYLE}>
          {isError ? t('ACTIVATE_CARD_ERROR_MESSAGE_SSN', { identityCard: nationalIdDescription || 'SSN' }) : ''}
        </div>

        <LiliButton
          type={LiliButtonType.BACK}
          onClick={() => {
            makeStepBack(
              params.tracked ?? '',
              () => {
                navigate(`/${CARD_TRACKER}`);
                goToStep(0);
              },
              () => goToStep(0),
            );
          }}
          title={t(ACTIVATE_STYLES_TEXTS.BACK_BUTTON)}
          className={ACTIVATE_STYLES_TEXTS.BACK_BUTTON_STYLE}
          id='cancelActivationButton'
        />
        <LiliButton
          onClick={() => {
            activateCard();
          }}
          className={ACTIVATE_STYLES_TEXTS.ACTIVATION_BUTTON_STYLE_SSN}
          id='continueActivationButton'
          disabled={disabledContinueButton || isActivationInProcess || isError}
          isLoading={isActivationInProcess || isRefetchingSummary}
          title={t(ACTIVATE_STYLES_TEXTS.ACTIVATION_BUTTON_TEXT)}
        />
      </div>
    </div>
  );
};

export default ActivationWithSsn;
